import { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import {
	LOAN_PURPOSE_BUSINESS,
	LOAN_PURPOSE_PERSONAL,
	ABN_STATUS_NEW_ABN,
	LOAN_PURPOSE_NOVATED_LEASE,
	LOAN_PURPOSE_EQUIPMENT,
	LOAN_PURPOSE_TRUCK,
	LOAN_PURPOSE_BUSINESS_VEHICLE,
	EMP_STAT_SELF_EMPLOYED,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";

export const AdditionalInfo = () => {
	const { formValues, setFormValue, setFormError } = useFormDataStore();
	const { enabledFields, setFormErrors, setValidateFormDetails, combinedIncomeMin } =
		useFormStateStore();

	const { loanPurpose, employmentStatus, abnStatus, gstStatus, creditHistory, combinedIncome } =
		formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		employmentStatus: false,
		abnStatus: false,
		gstStatus: false,
		creditHistory: false,
		combinedIncome: false,
	});

	const handleCombinedIncomeChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("combinedIncome", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				loanAmount: formattedValue < combinedIncomeMin,
			}));
		},
		[setFormValue, setFormError, setErrors],
	);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			employmentStatus:
				enabledFields.employmentStatus?.length == 0 ||
				[LOAN_PURPOSE_BUSINESS].includes(loanPurpose)
					? false
					: !employmentStatus,
			abnStatus:
				enabledFields.abnStatus?.length == 0 ||
				[LOAN_PURPOSE_PERSONAL, LOAN_PURPOSE_NOVATED_LEASE].includes(loanPurpose)
					? false
					: !abnStatus,
			gstStatus:
				enabledFields.gstStatus?.length == 0 ||
				abnStatus === ABN_STATUS_NEW_ABN ||
				[LOAN_PURPOSE_PERSONAL, LOAN_PURPOSE_NOVATED_LEASE].includes(loanPurpose)
					? false
					: !gstStatus,
			creditHistory: enabledFields.creditHistory?.length == 0 ? false : !creditHistory,
			combinedIncome:
				!combinedIncome || parseFloat(combinedIncome.replace(/,/g, "")) < combinedIncomeMin,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm's Next Button can trigger this.
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [employmentStatus, abnStatus, gstStatus, creditHistory, combinedIncome]);

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		// clearFormError(field as string);
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	return (
		<div className="eF-space-y-4">
			{enabledFields.employmentStatus?.length != 0 &&
				formValues.loanPurpose !== LOAN_PURPOSE_BUSINESS && (
					<RadioGroupField
						id="employmentStatus"
						label="What is your current employment status?"
						value={employmentStatus || ""}
						onChange={(value) => {
							setFormValue("employmentStatus", value);
							setErrors((prev) => ({ ...prev, employmentStatus: !value }));
						}}
						required
						error={errors.employmentStatus ? "This field is required" : ""}
						gridCols={3}
						showIcons={false}
						className="eF-mt-4 eF-justify-center eF-w-full"
						options={enabledFields.employmentStatus
							.filter((s) => s.value)
							.map((s) => ({
								id: s.value,
								value: s.value,
								label: s.option,
							}))}
					/>
				)}

			{enabledFields.abnStatus?.length != 0 &&
				[
					LOAN_PURPOSE_BUSINESS,
					LOAN_PURPOSE_BUSINESS_VEHICLE,
					LOAN_PURPOSE_EQUIPMENT,
					LOAN_PURPOSE_TRUCK,
				].includes(formValues.loanPurpose) && (
					<>
						<RadioGroupField
							id="abnStatus"
							label="How long has your ABN been running?"
							value={abnStatus || ""}
							onChange={(value) => {
								setFormValue("abnStatus", value);
								setFormValue("employmentStatus", EMP_STAT_SELF_EMPLOYED);
								setErrors((prev) => ({
									...prev,
									abnStatus: !value,
									gstStatus:
										value === ABN_STATUS_NEW_ABN ? false : prev.gstStatus,
								}));
								if (value === ABN_STATUS_NEW_ABN) {
									setFormValue("gstStatus", "");
								}
							}}
							required
							error={errors.abnStatus ? "This field is required" : ""}
							gridCols={3}
							showIcons={false}
							className="eF-mt-4 eF-justify-center eF-w-full"
							options={enabledFields.abnStatus
								.filter((s) => s.value)
								.map((s) => ({
									id: s.value,
									value: s.value,
									label: s.option,
								}))}
						/>

						{enabledFields.gstStatus?.length != 0 &&
							formValues.abnStatus !== ABN_STATUS_NEW_ABN && (
								<RadioGroupField
									id="gstStatus"
									label="How long have you been registered for GST?"
									value={gstStatus || ""}
									onChange={(value) => {
										setFormValue("gstStatus", value);
										setErrors((prev) => ({
											...prev,
											gstStatus: !value,
										}));
									}}
									required
									showIcons={false}
									error={errors.gstStatus ? "This field is required" : ""}
									gridCols={3}
									className="eF-mt-4 eF-justify-center eF-w-full"
									options={enabledFields.gstStatus
										.filter((s) => s.value)
										.map((s) => ({
											id: s.value,
											value: s.value,
											label: s.option,
										}))}
								/>
							)}
					</>
				)}

			{enabledFields.creditHistory?.length != 0 && (
				<RadioGroupField
					id="creditHistory"
					label="How would you rate your credit history?"
					value={creditHistory || ""}
					onChange={(value) => {
						setFormValue("creditHistory", value);
						setErrors((prev) => ({
							...prev,
							creditHistory: !value,
						}));
					}}
					required
					error={errors.creditHistory ? "This field is required" : ""}
					gridCols={3}
					showIcons={false}
					className="eF-mt-4 eF-justify-center eF-w-full"
					options={enabledFields.creditHistory
						.filter((s) => s.value)
						.map((s) => ({
							id: s.value,
							value: s.value,
							label: s.option,
						}))}
				/>
			)}

			{
				<div>
					<label
						htmlFor="grossIncome"
						className="eF-block eF-text-md eF-font-semibold eF-leading-6 eF-text-gray-900"
					>
						What is your combined yearly income?
					</label>
					<div className="eF-relative eF-mt-2 eF-rounded-md eF-shadow-sm">
						<div className="eF-pointer-events-none eF-absolute eF-inset-y-0 eF-left-0 eF-flex eF-items-center eF-pl-3">
							<span className="eF-text-gray-500 sm:eF-text-md">$</span>
						</div>
						<input
							value={combinedIncome}
							type="text"
							id="grossIncome"
							aria-describedby="combinedIncome"
							inputMode="numeric"
							aria-label="Combined Income"
							maxLength={12}
							onChange={handleCombinedIncomeChange}
							onFocus={() => handleFocus("combinedIncome")}
							className={clsx(
								errors.combinedIncome
									? "eF-ring-red-500 focus:eF-ring-red-500"
									: "eF-ring-gray-300 focus:eF-ring-[--eF-theme-color]",
								"eF-block eF-w-full eF-rounded-md eF-shadow-sm eF-border-0 eF-py-2.5 eF-pl-7 eF-pr-12 eF-text-gray-900 eF-ring-1 eF-ring-gray-300 focus:eF-ring-1 eF-outline-1 focus:eF-ring-inset focus-visible:eF-outline-[--eF-theme-color] placeholder:eF-text-gray-400 sm:eF-text-md sm:eF-leading-6",
							)}
						/>
						<div className="eF-pointer-events-none eF-absolute eF-inset-y-0 eF-right-0 eF-flex eF-items-center eF-pr-3">
							<span className="eF-text-gray-500 sm:eF-text-sm">AUD</span>
						</div>
					</div>
					{errors.combinedIncome && (
						<span className="eF-text-xs eF-text-red-500">
							Minimum combined yearly income is ${combinedIncomeMin.toLocaleString()}
						</span>
					)}
				</div>
			}
		</div>
	);
};
